import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type BookingType = 'browse' | 'instant';

export interface CleanerData {
  userid: string;
  firstname: string;
  lastname: string;
  profile_pic?: string;
  biography?: string;
  city: string;
  state: string;
  average_rating?: number;
  total_ratings?: number;
  total_completed_jobs?: number;
  distance?: number;
  availability?: Array<{
    id?: string;
    date?: string;
    start_time?: string;
    end_time?: string;
    is_recurring?: boolean;
    day_of_week?: number | null;
  }>;
  languages?: string[];
  work_preference?: string[];
}

export interface BookingState {
  booking_type: BookingType;
  userid: string;
  addressid: string;
  address: string;
  city: string;
  state: string;
  zipcode: string;
  latitude: number | undefined;
  longitude: number | undefined;
  booking_date: string;
  booking_time: string;
  booking_date_time: string;
  bedroom: number;
  bathroom: number;
  people: number;
  pets: number;
  cleaning_area: string;
  home_description: string;
  address_name: string;
  type_of_cleaning: string;
  house_rating: number;
  no_of_hours: number;
  no_of_cleaners: number;
  cleaners: string[];
  cleaners_data: CleanerData[];
  locked_cleaner_id: string;
  applied_promo_code: string;
}

interface BookingActions {
  setBookingType: (type: BookingType) => void;
  updateBooking: (partial: Partial<BookingState>) => void;
  clearBooking: () => void;
}

const initialState: BookingState = {
  booking_type: 'instant',
  userid: '',
  addressid: '',
  address: '',
  city: '',
  state: '',
  zipcode: '',
  latitude: undefined,
  longitude: undefined,
  booking_date: '',
  booking_time: '',
  booking_date_time: '',
  bedroom: 0,
  bathroom: 0,
  people: 0,
  pets: 0,
  cleaning_area: '',
  home_description: '',
  address_name: '',
  type_of_cleaning: '',
  house_rating: 0,
  no_of_hours: 0,
  no_of_cleaners: 0,
  cleaners: [],
  cleaners_data: [],
  locked_cleaner_id: '',
  applied_promo_code: '',
};

export const useBookingStore = create<BookingState & BookingActions>()(
  persist(
    set => ({
      ...initialState,

      setBookingType: (type: BookingType) =>
        set(state => {
          if (state.booking_type === type) return { booking_type: type };
          return {
            booking_type: type,
            booking_date: '',
            booking_time: '',
            booking_date_time: '',
            no_of_hours: 0,
            no_of_cleaners: 0,
            cleaners: [],
            cleaners_data: [],
            locked_cleaner_id: '',
            applied_promo_code: '',
          };
        }),

      updateBooking: (partial: Partial<BookingState>) =>
        set(state => ({ ...state, ...partial })),

      clearBooking: () =>
        set(() => {
          sessionStorage.removeItem('booking-storage');
          return { ...initialState };
        }),
    }),
    {
      name: 'booking-storage',
      storage: {
        getItem: name => {
          const str = sessionStorage.getItem(name);
          return str ? JSON.parse(str) : null;
        },
        setItem: (name, value) => {
          sessionStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: name => {
          sessionStorage.removeItem(name);
        },
      },
    }
  )
);
